/**
 * Application Constants
 * Centralized configuration values and constants used throughout the application
 */

import { env } from './env';

import type {
  AgeRangeType as ProfileAgeRangeType,
  NotificationType,
  ServiceType as ProfileServiceType,
} from '@/types/profile';

// ============================================================================
// API Configuration
// ============================================================================

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/token/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/token/logout',
    REFRESH: '/auth/refresh',
    VERIFY_EMAIL: '/auth/verify',
    RESEND_VERIFICATION: '/auth/request-verify-token',
  },

  // Caregiver Management
  CAREGIVERS: {
    ME: '/api/caregivers/me',
    ONBOARDING: {
      PERSONAL_INFO: '/api/caregivers/onboarding/personal-info',
      SERVICES: '/api/caregivers/onboarding/services',
      COMPLETE: '/api/caregivers/onboarding/complete',
    },
    AVAILABILITY: '/api/v1/me/availability-slots',
    BOOKING_REQUESTS: '/api/caregivers/me/booking-requests',
    BOOKINGS: '/api/v1/me/bookings',
  },

  // Public API
  PUBLIC: {
    CAREGIVER: (linkCode: string) => `/api/caregivers/${linkCode}`,
    AVAILABILITY: (linkCode: string) => `/api/v1/public/availability-slots/${linkCode}`,
    BOOKING_REQUEST: (linkCode: string) => `/api/caregivers/${linkCode}/booking-requests`,
  },
} as const;

// ============================================================================
// UI Configuration
// ============================================================================

export const UI_CONFIG = {
  // Toast Configuration
  TOAST: {
    DURATION: 4000, // 4 seconds
    POSITION: {
      DESKTOP: 'top-right' as const,
      MOBILE: 'top-center' as const,
    },
  },

  // Modal Configuration
  MODAL: {
    ANIMATION_DURATION: 200, // milliseconds
    BACKDROP_BLUR: true,
  },

  // Form Configuration
  FORM: {
    DEBOUNCE_DELAY: 300, // milliseconds
    VALIDATION_DELAY: 500, // milliseconds
  },

  // Loading States
  LOADING: {
    SKELETON_ANIMATION_DURATION: 1500, // milliseconds
    SPINNER_SIZE: {
      SMALL: 16,
      MEDIUM: 24,
      LARGE: 32,
    },
  },

  // Responsive Breakpoints (matching Tailwind CSS)
  BREAKPOINTS: {
    SM: 640,
    MD: 768,
    LG: 1024,
    XL: 1280,
    '2XL': 1536,
  },

  // Touch Targets (minimum size for mobile)
  TOUCH_TARGET: {
    MIN_SIZE: 44, // pixels
  },
} as const;

// ============================================================================
// Business Logic Constants
// ============================================================================

type NonEmptyReadonlyArray<T> = readonly [T, ...T[]];
type ServiceTypeOption = {
  value: ProfileServiceType;
  label: string;
};
type AgeRangeOption = {
  type: ProfileAgeRangeType;
  label: string;
};

const SERVICE_TYPES: NonEmptyReadonlyArray<ProfileServiceType> = [
  'BABYSITTING',
  'OCCASIONAL_NANNY',
  'FAMILY_ASSISTANT',
  'COMPANION_SERVICES',
  'DOULA',
  'NEWBORN_CARE_SPECIALIST',
  'SLEEP_SERVICES',
  'PET_CARE',
  'TUTORING',
  'OTHER_SERVICES',
];

const SERVICE_TYPE_OPTIONS: ReadonlyArray<ServiceTypeOption> = [
  { value: 'BABYSITTING', label: 'Babysitting' },
  { value: 'OCCASIONAL_NANNY', label: 'Occasional Nanny' },
  { value: 'FAMILY_ASSISTANT', label: 'Family Assistant' },
  { value: 'COMPANION_SERVICES', label: 'Elder Companion' },
  { value: 'DOULA', label: 'Doula' },
  { value: 'NEWBORN_CARE_SPECIALIST', label: 'Newborn Care Specialist' },
  { value: 'SLEEP_SERVICES', label: 'Sleep Services' },
  { value: 'PET_CARE', label: 'Pet Care' },
  { value: 'TUTORING', label: 'Tutoring' },
  { value: 'OTHER_SERVICES', label: 'Other Services' },
];

const AGE_RANGE_TYPES: NonEmptyReadonlyArray<ProfileAgeRangeType> = [
  'INFANT',
  'TODDLER',
  'PRESCHOOL',
  'SCHOOL_AGE_YOUNG',
  'SCHOOL_AGE_OLDER',
  'TEEN',
  'SENIOR_YOUNG',
  'SENIOR_MID',
  'SENIOR_ADVANCED',
];

const AGE_RANGE_OPTIONS: ReadonlyArray<AgeRangeOption> = [
  { type: 'INFANT', label: 'Infants (0-1 years)' },
  { type: 'TODDLER', label: 'Toddlers (1-3 years)' },
  { type: 'PRESCHOOL', label: 'Preschool (3-5 years)' },
  { type: 'SCHOOL_AGE_YOUNG', label: 'School Age (5-8 years)' },
  { type: 'SCHOOL_AGE_OLDER', label: 'School Age (9-12 years)' },
  { type: 'TEEN', label: 'Teens (13+ years)' },
  { type: 'SENIOR_YOUNG', label: 'Seniors (65-74 years)' },
  { type: 'SENIOR_MID', label: 'Seniors (75-84 years)' },
  { type: 'SENIOR_ADVANCED', label: 'Seniors (85+ years)' },
];

export const BUSINESS_CONFIG = {
  // Service Types (matching backend enum values)
  SERVICE_TYPES,

  SERVICE_TYPE_OPTIONS,

  AGE_RANGE_TYPES,

  AGE_RANGE_OPTIONS,

  // Booking Status
  BOOKING_STATUS: {
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    DECLINED: 'declined',
    CONFIRMED: 'confirmed',
    COMPLETED: 'completed',
    CANCELED: 'canceled',
  } as const,

  // Availability Configuration
  AVAILABILITY: {
    // Time slots in 15-minute intervals
    TIME_SLOT_INTERVAL: 15, // minutes
    MIN_SLOT_DURATION: 60, // minutes (1 hour)
    MAX_SLOT_DURATION: 480, // minutes (8 hours)

    // Business hours
    BUSINESS_HOURS: {
      START: '06:00',
      END: '22:00',
    },

    // Week configuration
    WEEK_STARTS_ON: 0, // Sunday (0 = Sunday, 1 = Monday)
    DAYS_IN_WEEK: 7,
    MILLISECONDS_IN_DAY: 24 * 60 * 60 * 1000,
    MILLISECONDS_IN_WEEK: 7 * 24 * 60 * 60 * 1000,
    MINUTES_IN_HOUR: 60,
    SECONDS_IN_MINUTE: 60,
    MILLISECONDS_IN_SECOND: 1000,
    MILLISECONDS_IN_MINUTE: 60 * 1000,
  },

  // Onboarding Configuration
  ONBOARDING: {
    TOTAL_STEPS: 2,
    STEPS: [
      { step: 1, path: '/onboarding/personal-info', label: 'Personal Info' },
      { step: 2, path: '/onboarding/terms', label: 'Terms & Conditions' },
    ] as { step: number; path: string; label: string }[],
    AUTO_REDIRECT_DELAY: 3000, // milliseconds
  },

  // Profile Configuration
  PROFILE: {
    ABOUT_ME: {
      MIN_LENGTH: 50, // characters
      MAX_LENGTH: 800, // characters
      SUGGESTED_SENTENCES: '3-5 sentences',
    },

    SERVICE_AREAS: {
      MAX_LENGTH: 255,
    },

    PHONE_NUMBER: {
      PATTERN: /^\(\d{3}\) \d{3}-\d{4}$/, // (555) 123-4567 format
    },
  },
} as const;

export const NOTIFICATION_OPTIONS: Array<{ label: string; value: NotificationType }> = [
  { label: 'Text', value: 'TEXT' },
  { label: 'Email', value: 'EMAIL' },
];

// ============================================================================
// Environment-Specific Constants
// ============================================================================

export const ENV_CONFIG = {
  // Development-specific constants
  DEV: {
    MOCK_DELAY: env.isDevelopment ? 500 : 0, // milliseconds
    ENABLE_REACT_DEVTOOLS: env.isDevelopment,
    SHOW_DEBUG_INFO: env.features.enableDebugLogging,
  },

  // Production-specific constants
  PROD: {
    ENABLE_ANALYTICS: env.features.enableAnalytics,
    ENABLE_ERROR_TRACKING: env.features.enableErrorTracking,
    ENABLE_PERFORMANCE_MONITORING: env.features.enablePerformanceMonitoring,
  },

  // Feature flags
  FEATURES: env.features,
} as const;

// ============================================================================
// Validation Constants
// ============================================================================

export const VALIDATION = {
  // Email validation
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MAX_LENGTH: 254,
  },

  // Password validation
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBER: true,
    REQUIRE_SPECIAL_CHAR: false,
    PATTERN: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  },

  // Name validation
  NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 50,
    PATTERN: /^[a-zA-Z\s'-]+$/,
  },

  // Phone number validation
  PHONE: {
    PATTERN: /^\(\d{3}\) \d{3}-\d{4}$/,
    DIGITS_ONLY_PATTERN: /^\d{10}$/,
  },

  // Service area validation
  SERVICE_AREA: {
    MAX_LENGTH: BUSINESS_CONFIG.PROFILE.SERVICE_AREAS.MAX_LENGTH,
  },
} as const;

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  // Generic errors
  GENERIC: {
    NETWORK_ERROR: 'Network error. Please check your connection and try again.',
    SERVER_ERROR: 'Something went wrong on our end. Please try again later.',
    UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
    SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  },

  // Validation errors
  VALIDATION: {
    REQUIRED: 'This field is required',
    INVALID_EMAIL: 'Please enter a valid email address',
    INVALID_PHONE: 'Please enter a valid phone number',
    INVALID_SERVICE_AREA: 'Please enter a valid service area',
    PASSWORD_TOO_SHORT: `Password must be at least ${VALIDATION.PASSWORD.MIN_LENGTH} characters`,
    PASSWORD_WEAK: 'Password must contain uppercase, lowercase, and number',
    PASSWORDS_DONT_MATCH: 'Passwords do not match',
    NAME_TOO_SHORT: `Name must be at least ${VALIDATION.NAME.MIN_LENGTH} characters`,
    NAME_TOO_LONG: `Name must be no more than ${VALIDATION.NAME.MAX_LENGTH} characters`,
    INVALID_NAME: 'Name can only contain letters, spaces, hyphens, and apostrophes',
  },

  // Business logic errors
  BUSINESS: {
    INVALID_TIME_RANGE: 'End time must be after start time',
    SLOT_TOO_SHORT: `Availability slot must be at least ${BUSINESS_CONFIG.AVAILABILITY.MIN_SLOT_DURATION} minutes`,
    SLOT_TOO_LONG: `Availability slot cannot exceed ${BUSINESS_CONFIG.AVAILABILITY.MAX_SLOT_DURATION} minutes`,
    OVERLAPPING_SLOTS: 'This time slot overlaps with an existing availability',
    PAST_DATE: 'Cannot create availability for past dates',
    SERVICE_AREA_TOO_LONG: `Service area must be no more than ${BUSINESS_CONFIG.PROFILE.SERVICE_AREAS.MAX_LENGTH} characters`,
    ABOUT_ME_TOO_SHORT: `About me section must be at least ${BUSINESS_CONFIG.PROFILE.ABOUT_ME.MIN_LENGTH} characters`,
    ABOUT_ME_TOO_LONG: `About me section must be no more than ${BUSINESS_CONFIG.PROFILE.ABOUT_ME.MAX_LENGTH} characters`,
  },
} as const;

// ============================================================================
// Success Messages
// ============================================================================

export const SUCCESS_MESSAGES = {
  AUTH: {
    REGISTRATION_SUCCESS:
      'Account created successfully! Please check your email to verify your account.',
    LOGIN_SUCCESS: 'Welcome back!',
    LOGOUT_SUCCESS: 'You have been logged out successfully.',
    EMAIL_VERIFIED: 'Email verified successfully!',
    VERIFICATION_SENT: 'Verification email sent. Please check your inbox.',
  },

  ONBOARDING: {
    PERSONAL_INFO_SAVED: 'Personal information saved successfully!',
    SERVICES_SAVED: 'Services information saved successfully!',
    ONBOARDING_COMPLETE: 'Profile setup complete! Welcome to CareCal.',
  },

  AVAILABILITY: {
    SLOT_CREATED: 'Availability slot created successfully!',
    SLOT_UPDATED: 'Availability slot updated successfully!',
    SLOT_DELETED: 'Availability slot deleted successfully!',
    MASTER_TOGGLE_UPDATED: 'Availability status updated successfully!',
  },

  BOOKING: {
    REQUEST_SENT: 'Booking request sent successfully!',
    REQUEST_ACCEPTED: 'Booking request accepted successfully!',
    REQUEST_DECLINED: 'Booking request declined successfully!',
    BOOKING_CANCELLED: 'Booking canceled successfully!',
  },

  PROFILE: {
    LINK_COPIED: 'Profile link copied to clipboard!',
    ACCESS_UPDATED: 'Profile access updated successfully!',
  },
} as const;

// ============================================================================
// Route Constants
// ============================================================================

export const ROUTES = {
  // Public routes
  HOME: '/',
  CAREGIVER_PROFILE: (linkCode: string) => `/book/${linkCode}`,

  // Auth routes
  LOGIN: '/login',
  REGISTER: '/register',
  VERIFY_EMAIL: '/verify-email',
  VERIFY_EMAIL_SUCCESS: '/verify-email-success',

  // Onboarding routes
  ONBOARDING: {
    PERSONAL_INFO: '/onboarding/personal-info',
    TERMS: '/onboarding/terms',
  },

  // Dashboard routes
  DASHBOARD: '/dashboard',
  CALENDAR: '/dashboard/calendar',
  BOOKINGS: '/dashboard/bookings',

  // Error routes
  NOT_FOUND: '/404',
  SERVER_ERROR: '/500',

  // Development routes
  DESIGN_SYSTEM: '/design-system',
  ONBOARDING_DEMO: '/onboarding-demo',
  LAYOUT_TEST: '/layout-test',
} as const;

// ============================================================================
// Tab Configuration Constants
// ============================================================================

export const TAB_CONFIG = {
  BOOKING_REQUESTS: {
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    DECLINED: 'declined',
    ALL: 'all',
  },

  MY_BOOKINGS: {
    UPCOMING: 'upcoming',
    PAST: 'past',
    CANCELED: 'canceled',
  },
} as const;

// ============================================================================
// Authentication Configuration
// ============================================================================

export const AUTH_CONFIG = {
  // Token validation on app startup
  VALIDATE_TOKEN_ON_STARTUP: true,

  // Whether to logout on network errors during token validation
  // Set to false to be resilient to temporary connectivity issues
  LOGOUT_ON_NETWORK_ERROR: false,

  // Zustand persist storage key
  STORAGE_KEY: 'carecal-auth',

  // Token lifetime in milliseconds (must match backend ACCESS_TOKEN_LIFETIME_SECONDS)
  // Default: 7 days = 604,800 seconds = 604,800,000 ms
  TOKEN_LIFETIME_MS: 7 * 24 * 60 * 60 * 1000,

  // Proactive refresh threshold (refresh when this % of lifetime has passed)
  // 0.8 = refresh after 80% of lifetime (5.6 days for 7-day token)
  REFRESH_THRESHOLD: 0.8,

  // Minimum time between refresh attempts (prevents rapid retries on errors)
  MIN_REFRESH_INTERVAL_MS: 60 * 1000, // 1 minute

  // Visibility refresh threshold (refresh if tab was hidden longer than this)
  VISIBILITY_REFRESH_THRESHOLD_MS: 30 * 60 * 1000, // 30 minutes
} as const;

// ============================================================================
// Local Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'carecal_auth_token',
  REFRESH_TOKEN: 'carecal_refresh_token',
  USER_PREFERENCES: 'carecal_user_preferences',
  ONBOARDING_PROGRESS: 'carecal_onboarding_progress',
  THEME: 'carecal_theme',
  CLIENT_BOOKING_TERMS_VERSION: 'carecal_client_booking_terms_version',
  PUBLIC_BOOKING_AUTH: 'carecal_public_booking_auth',
} as const;

// ============================================================================
// SWR Configuration Constants
// ============================================================================

export const SWR_CONFIG = {
  // Cache durations in milliseconds
  DEDUPE_INTERVAL: 2 * 60 * 1000, // 2 minutes
  REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes
  FOCUS_THROTTLE_INTERVAL: 5000, // 5 seconds

  // Retry configuration
  ERROR_RETRY_COUNT: 3,
  ERROR_RETRY_INTERVAL: 1000, // 1 second

  // Revalidation settings
  REVALIDATE_ON_FOCUS: false,
  REVALIDATE_ON_RECONNECT: true,
  SHOULD_RETRY_ON_ERROR: true,
} as const;

// ============================================================================
// Link Code Configuration
// ============================================================================

export const LINK_CODE_CONFIG = {
  // Pattern: letters-letters-numbers (e.g., "sarah-j-12345")
  PATTERN: /^[a-z0-9]+-[a-z0-9]+-\d{5}$/,
  RANDOM_SUFFIX_LENGTH: 5,
  SEPARATOR: '-',
} as const;

export function getServiceTypeLabel(serviceType: string): string {
  return (
    BUSINESS_CONFIG.SERVICE_TYPE_OPTIONS.find(option => option.value === serviceType)?.label ??
    serviceType
  );
}

export function getAgeRangeLabel(ageRangeType: ProfileAgeRangeType): string {
  return (
    BUSINESS_CONFIG.AGE_RANGE_OPTIONS.find(option => option.type === ageRangeType)?.label ??
    ageRangeType
  );
}

// ============================================================================
// Type Exports
// ============================================================================

export type ServiceType = (typeof BUSINESS_CONFIG.SERVICE_TYPES)[number];
export type BookingStatus =
  (typeof BUSINESS_CONFIG.BOOKING_STATUS)[keyof typeof BUSINESS_CONFIG.BOOKING_STATUS];
export type ApiEndpoint = (typeof API_ENDPOINTS)[keyof typeof API_ENDPOINTS];
export type Route = (typeof ROUTES)[keyof typeof ROUTES];
export type BookingRequestTab =
  (typeof TAB_CONFIG.BOOKING_REQUESTS)[keyof typeof TAB_CONFIG.BOOKING_REQUESTS];
export type MyBookingsTab = (typeof TAB_CONFIG.MY_BOOKINGS)[keyof typeof TAB_CONFIG.MY_BOOKINGS];

// ============================================================================
// Compliance Copy
// ============================================================================

export const OPTIN_COPY =
  'I agree to receive booking notifications via text or email (message and data rates may apply): *';
